import { useState } from "react";
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Boxes,
  Building2,
  Users,
  BarChart3,
  Settings,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
} from "lucide-react";

/**
 * Admin Left Sidebar — iter204.
 * Scope: ADMIN navigation only. Never rendered on public storefront.
 * Visual language: dark charcoal panel, ~215px wide, restrained line
 * icons, muted oxblood active-state indicator. Locked build per Greg's
 * approval. Do not add new primary nav items here.
 */

const PRIMARY = [
  { id: "",                    key: "dashboard",       label: "Dashboard",       icon: LayoutDashboard },
  { id: "orders",              key: "orders",          label: "Orders",          icon: ShoppingCart    },
  { id: "products",            key: "products",        label: "Products",        icon: Package         },
  { id: "inventory",           key: "inventory",       label: "IMS",             icon: Boxes           },
  { id: "suppliers-directory", key: "suppliers",       label: "Suppliers",       icon: Building2       },
  { id: "customers",           key: "customers",       label: "Customers",       icon: Users           },
  { id: "reports",             key: "reports",         label: "Reports",         icon: BarChart3       },
  { id: "settings",            key: "settings",        label: "Settings",        icon: Settings        },
];

const MORE = [
  { id: "fulfillment",    label: "Ready to Ship" },
  { id: "purchase-orders",label: "Purchase Orders" },
  { id: "trials",         label: "Trials" },
  { id: "codes",          label: "Discount Codes" },
  { id: "compliance",     label: "Compliance" },
  { id: "messages",       label: "Messages" },
  { id: "banner",         label: "Banner" },
  { id: "snippets",       label: "Snippets" },
  { id: "mailing",        label: "Mailing" },
  { id: "access",         label: "Access Requests" },
  { id: "redirects",      label: "Redirects" },
];

export default function AdminSidebar({ activeTab, onNavigate }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const isActive = (id) => activeTab === id;

  const NavItem = ({ item }) => {
    const active = isActive(item.id);
    const Icon = item.icon;
    return (
      <button
        onClick={() => onNavigate(item.id)}
        className={`w-full flex items-center gap-3 px-4 py-2.5 text-[13.5px] transition-colors relative ${
          active
            ? "bg-[#3a1a20] text-white font-semibold"
            : "text-neutral-300 hover:bg-neutral-800 hover:text-white"
        }`}
        data-testid={`admin-nav-${item.key}`}
      >
        {active && (
          <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#8B1E2E]" />
        )}
        <Icon size={16} strokeWidth={1.75} className={active ? "text-white" : "text-neutral-400"} />
        <span className="tracking-tight">{item.label}</span>
      </button>
    );
  };

  return (
    <aside
      className="w-[215px] shrink-0 bg-[#0f1113] text-neutral-200 flex flex-col min-h-screen border-r border-black/40"
      data-testid="admin-sidebar"
    >
      {/* Brand */}
      <div className="px-5 py-6 flex items-center gap-2.5 border-b border-neutral-800">
        <span className="inline-flex items-center justify-center w-8 h-8 border border-white/70 text-white font-serif text-[13px] tracking-tight">
          BBK
        </span>
        <span className="text-white font-serif text-[13.5px] tracking-[0.14em] uppercase">Bulk Buy Kits</span>
      </div>

      {/* Admin label */}
      <div className="px-5 pt-5 pb-2">
        <span className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-semibold">Admin</span>
      </div>

      {/* Primary nav */}
      <nav className="flex-1 flex flex-col py-1" data-testid="admin-primary-nav">
        {PRIMARY.map((it) => <NavItem key={it.key} item={it} />)}

        {/* More collapsible */}
        <button
          onClick={() => setMoreOpen((v) => !v)}
          className="mt-2 w-full flex items-center justify-between gap-3 px-4 py-2.5 text-[12.5px] tracking-[0.14em] uppercase text-neutral-400 hover:text-neutral-200 transition-colors"
          data-testid="admin-nav-more-toggle"
        >
          <span className="flex items-center gap-3">
            <MoreHorizontal size={14} strokeWidth={1.75} /> More
          </span>
          {moreOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        {moreOpen && (
          <div className="pb-2" data-testid="admin-nav-more-panel">
            {MORE.map((m) => {
              const active = activeTab === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => onNavigate(m.id)}
                  className={`w-full text-left pl-11 pr-4 py-1.5 text-[12.5px] transition-colors ${
                    active
                      ? "bg-[#3a1a20] text-white font-semibold"
                      : "text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
                  }`}
                  data-testid={`admin-nav-more-${m.id}`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        )}
      </nav>

      {/* Footer brand */}
      <div className="px-5 py-4 border-t border-neutral-800">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-semibold">Bulk Buy Kits</div>
        <div className="text-[11px] text-neutral-500 mt-1">Research Supply. Simplified.</div>
        <div className="text-[10px] text-neutral-600 mt-3">Admin v1.0</div>
      </div>
    </aside>
  );
}
